import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useHistory } from "react-router-dom";

import {
  ShiftRateNegotiationRole,
  ShiftRateNegotiationStatus,
} from "../OpenShifts/RateNegotiation/types";
import { Button } from "../redesign/components/Button";
import { DialogFooter } from "../redesign/components/DialogFooter";
import { HoldToConfirmButton } from "../redesign/components/HoldToConfirmButton";
import { BookShiftButton } from "../redesign/Shift/BottomSheet/BookShiftButton";
import { type ModalShiftData, type ModalShiftRateNegotiationData } from "../redesign/Shift/types";
import { SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH } from "../redesign/ShiftDiscovery/paths";
import { type NavigateToModalFunction } from "../redesign/ShiftDiscovery/useShiftModalsDataContext";
import { NegotiationHistoryAction, NegotiationRateRole } from "./types";

interface RateNegotiationDialogFooterProps {
  currentRate: number;
  shift: ModalShiftData;
  lowerLimit: number;
  upperLimit: number;
  isBookable: boolean;
  isInstantBookDisabled: boolean;
  increaseRate: () => void;
  decreaseRate: () => void;
  handleProposeNewRate: () => void;
  handleEndNegotiation: () => void;
  navigateToModal: NavigateToModalFunction;
  isCreatingNegotiation: boolean;
}

function getTitleText(parameters: {
  rateNegotiation: ModalShiftRateNegotiationData | undefined;
  lowerLimit: number;
  upperLimit: number;
}): string {
  const { rateNegotiation, lowerLimit, upperLimit } = parameters;

  if (rateNegotiation?.lastOfferBy === ShiftRateNegotiationRole.WORKER) {
    return "Waiting for Workplace";
  }

  if (
    lowerLimit >= upperLimit &&
    rateNegotiation?.lastOfferBy === ShiftRateNegotiationRole.WORKPLACE
  ) {
    return "Please End or Book";
  }

  return "Propose Rate";
}

function getButtonText(parameters: { currentRate: number; lowerLimit: number }): string {
  const { currentRate, lowerLimit } = parameters;

  if (currentRate !== lowerLimit) {
    return `Press and hold to propose ${formatDollarsAsUsd(currentRate)}/hr`;
  }

  return `Book at ${formatDollarsAsUsd(lowerLimit)}/hr`;
}

export function RateNegotiationDialogFooter(props: RateNegotiationDialogFooterProps) {
  const history = useHistory();
  const {
    currentRate,
    shift,
    increaseRate,
    decreaseRate,
    handleProposeNewRate,
    isCreatingNegotiation,
    lowerLimit,
    upperLimit,
    handleEndNegotiation,
    isBookable,
    isInstantBookDisabled,
    navigateToModal,
  } = props;

  const rateIsDifferent = currentRate !== lowerLimit;
  const { id: shiftId, rateNegotiation } = shift;
  const isProposedByWorker = rateNegotiation?.lastOfferBy === ShiftRateNegotiationRole.WORKER;

  const negotiationIsBookable = (rateNegotiation?.history ?? []).some((historyItem) => {
    return (
      historyItem.createdByUserRole === NegotiationRateRole.WORKPLACE &&
      historyItem.action === NegotiationHistoryAction.PROPOSED
    );
  });

  const rateNegotiationStatus = rateNegotiation?.status;
  const rateNegotiationId = rateNegotiation?.id;

  if (
    isDefined(rateNegotiationStatus) &&
    rateNegotiationStatus !== ShiftRateNegotiationStatus.OPEN
  ) {
    return (
      <DialogFooter withShadow orientation="vertical" sx={{ marginBottom: 4 }}>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          onClick={() => {
            history.goBack();
          }}
        >
          Close
        </Button>
      </DialogFooter>
    );
  }

  return (
    <DialogFooter withShadow orientation="vertical" sx={{ marginBottom: 4 }}>
      <Stack direction="column" alignItems="center" gap={5} sx={{ width: "100%" }}>
        <Text semibold variant="h6">
          {getTitleText({ rateNegotiation, lowerLimit, upperLimit })}
        </Text>

        <Text variant="body1" sx={{ textAlign: "center" }}>
          If the workplace accepts your proposal you will be booked for this shift.
        </Text>

        <Stack direction="row" alignItems="center" gap={10}>
          <IconButton
            iconType="minus"
            variant="outlined"
            disabled={isProposedByWorker || currentRate <= lowerLimit}
            aria-label="Decrease proposed hourly rate"
            onClick={decreaseRate}
          />
          <HourlyPayLabel size="medium" hourlyPay={formatDollarsAsUsd(currentRate)} />
          <IconButton
            iconType="plus"
            variant="outlined"
            disabled={isProposedByWorker || currentRate + 1 > upperLimit}
            aria-label="Increase proposed hourly rate"
            onClick={increaseRate}
          />
        </Stack>

        <Stack direction="row" gap={10} sx={{ width: "100%" }} justifyContent="center">
          {rateNegotiationStatus === ShiftRateNegotiationStatus.OPEN && (
            <Button fullWidth size="large" variant="outlined" onClick={handleEndNegotiation}>
              End negotiation
            </Button>
          )}

          {rateIsDifferent && !isProposedByWorker && (
            <HoldToConfirmButton
              fullWidth
              isLoading={isCreatingNegotiation}
              disabled={isCreatingNegotiation}
              onConfirm={handleProposeNewRate}
            >
              {getButtonText({ currentRate, lowerLimit })}
            </HoldToConfirmButton>
          )}

          {negotiationIsBookable &&
            !rateIsDifferent &&
            isBookable &&
            !isInstantBookDisabled &&
            rateNegotiationId && (
              <BookShiftButton
                fullWidth
                isInstantBookDisabled={isInstantBookDisabled}
                disabled={!isBookable}
                shift={shift}
                rateNegotiationId={rateNegotiationId}
                onBook={() => {
                  navigateToModal(SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH, {
                    pathParams: { shiftId },
                    clearExistingSearchParams: true,
                  });
                }}
              />
            )}
        </Stack>
      </Stack>
    </DialogFooter>
  );
}
